import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import theme from './BuyerChangeRequestSubmit.module.scss';
import { IState } from '@/state/IState';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
    OfferSignatureInfo,
    prepareOfferSignature,
    getChangeRequestProgress,
    signOfferSignature,
} from '@/services/buyer-dashboard';
import { InternalLinkButton } from '@/components/Buttons';
import { useIsMobile, useAsyncState, useAppDispatch } from '@/utils/hooks';
import { getUserSettings } from '@/services/users';
import { AlertBanner } from '@ygtechnologies/rubus-ui';
import { SimpleCard } from '@/components/Cards';
import ConfirmIdentity from '@/buyer/components/ConfirmIdentity';
import BuyerChangeRequestOffer from '../BuyerChangeRequestOffer';
import { requestOneTimeCode } from '@/services/signatures';
import { loadBuyerChangeRequests } from '@/state/buyer/changeRequests/creator';
import { LoadingIndicator, StepWizard } from '@ygtechnologies/bygr-frontend-component-library';
import { SimplePageTitle } from '@/components/layout/PageTitle';
import { classnames } from '@/utils/html';

type Props = {
    // buyerOfferId: number;
    changeRequestId: number;
    buyerOfferId: number;
};

const BuyerChangeRequestSubmit: React.FC<Props> = ({ changeRequestId, buyerOfferId }) => {
    const isMobile = useIsMobile();
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [userSettings] = useAsyncState(() => getUserSettings(), [], null);
    const projectData = useSelector((state: IState) => state.buyer.projectData!);
    const residentialUnitId = useSelector((state: IState) => state.buyer.permissions!.residentialUnitId);
    const [oneTimeCodeError, setOneTimeCodeError] = useState(false);
    const dispatch = useAppDispatch();

    const [changeRequest] = useAsyncState(
        () => getChangeRequestProgress(residentialUnitId, changeRequestId),
        [residentialUnitId, changeRequestId],
        null
    );

    const [signatureInfo, setSignatureInfo] = useState<OfferSignatureInfo | null>(null);

    useEffect(() => {
        async function loadData() {
            if (buyerOfferId) {
                setSignatureInfo(await prepareOfferSignature(residentialUnitId, buyerOfferId));
            } else {
                setSignatureInfo(null);
            }
        }

        loadData();
    }, [residentialUnitId, buyerOfferId]);

    const handleSend = async () => {
        setOneTimeCodeError(false);
        setLoading(true);
        try {
            await signOfferSignature(residentialUnitId, signatureInfo!.clientKey, code);
            dispatch(loadBuyerChangeRequests());
            setSent(true);
            return true;
        } catch (e: any) {
            // Wrong error code
            if (e.status === 400) {
                // Toast.error(<Trans>One time code is not correct</Trans>, null, { autoClose: false });
                setOneTimeCodeError(true);
            } else {
                // Toast.error(<Trans>Something went wrong</Trans>, null, { autoClose: false });
                throw e;
            }
            return false;
        } finally {
            setLoading(false);
        }
    };

    if (!signatureInfo || !changeRequest) {
        return <LoadingIndicator />;
    }

    const buyerOffer = changeRequest.offers.find((x) => x.id === signatureInfo.buyerOfferId)!;

    return (
        <div className={theme.container}>
            {!isMobile && <SimplePageTitle title={t`Accept offer #${buyerOffer.id} "${changeRequest.subject}"`} />}
            {isMobile && (
                <>
                    <SimplePageTitle title={t`Change requests`} />
                    <h6>
                        <Trans>Accept offer #{buyerOffer.id}</Trans>
                    </h6>
                </>
            )}
            <div className={theme.wizardContainer}>
                <StepWizard>
                    <StepWizard.Step
                        title={
                            <Trans>
                                Confirm offer #{buyerOffer.id} "{changeRequest.subject}"
                            </Trans>
                        }
                        hideBack
                        disableNext={userSettings !== null && !userSettings.phoneNumber}
                        buttonClassName={theme.buttons}
                        nextText={<Trans>Confirm this offer</Trans>}
                        onNextCallBack={() => requestOneTimeCode(signatureInfo.clientKey)}
                        buttonSize="lg">
                        <AlertBanner type="info" title={<Trans>Sign offer</Trans>}>
                            <Trans>
                                You are signing now the offer you received for the change request you have sent - "
                                {changeRequest.subject}".
                            </Trans>
                        </AlertBanner>
                        <BuyerChangeRequestOffer offer={buyerOffer} changeRequestId={changeRequestId} />
                    </StepWizard.Step>
                    <StepWizard.Step
                        disableNext={code.length !== 5 || loading}
                        nextText={<Trans>Click here to sign</Trans>}
                        buttonClassName={theme.buttons}
                        hideBack
                        nextPreCheck={() => handleSend().then((r) => r)}
                        // onNextCallBack={handleSend}
                        title={<Trans>Confirm Identity</Trans>}
                        buttonSize="lg">
                        <ConfirmIdentity
                            hasError={oneTimeCodeError}
                            code={code}
                            onCodeChange={setCode}
                            onResendClick={() => requestOneTimeCode(signatureInfo.clientKey)}
                            message={
                                <Trans>
                                    Please enter the five digit code that we have sent your to your phone to confirm your
                                    identity as the buyer of {projectData.unitName} at {projectData.projectName}.
                                </Trans>
                            }
                        />
                    </StepWizard.Step>
                    <StepWizard.Step hideBack title={<Trans>Thank you</Trans>}>
                        <SimpleCard>
                            <div>
                                <h5>
                                    <Trans>Thank you for your order</Trans>
                                </h5>
                                <p>
                                    <Trans>
                                        Your order has been placed. You can always see the products you ordered on the budget
                                        page.
                                    </Trans>
                                </p>
                            </div>
                        </SimpleCard>
                    </StepWizard.Step>
                </StepWizard>
                <div className={classnames(theme.buttons, theme.signButtonContainer)}>
                    <InternalLinkButton
                        color="primary"
                        size="xlarge"
                        to={`/change-requests/${changeRequestId}`}
                        disabled={!sent}>
                        <Trans>Back to change requests</Trans>
                    </InternalLinkButton>
                </div>
            </div>
        </div>
    );
};

export default BuyerChangeRequestSubmit;
